import React, { useCallback, useContext, useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { RouterRoutes } from "../utils/RouterRoutes";
import { useLocation } from "react-router-dom";

const descriptionMetaContent = [
    {
        route: RouterRoutes.login,
        content: 'Login to Wonga.'
    },
    {
        route: RouterRoutes.myLoan,
        content: 'View your Wonga account.'
    },
    {
        route: RouterRoutes.recoverPassword,
        content: 'Recover your Wonga account password.'
    },
    {
        route: RouterRoutes.confirmPasswordReset,
        content: 'Reset your Wonga account password.'
    },
    {
        route: RouterRoutes.register,
        content: 'Register for a Wonga account to access short term loans up to R5000 for new customers.'
    },
    {
        route: 'Default',
        content: 'Short term loans from R500 - R5000. Fast, flexible and 100% online. No branch visits required, no hidden charges. Quick online decision, cash straight to your bank account if approved.'
    }
];

interface SEOContextValue {
    setSEO: (meta: { title?: string; description?: string; noIndex?: boolean; canonicalUrl?: string }) => void;
}

export const SeoContext = React.createContext<SEOContextValue | null>(null);

export const useSeoContext = () => {
    return useContext(SeoContext);
};

interface SeoContextProps {
    children: React.ReactNode
}

export const SeoContextProvider = ({ children }: SeoContextProps) => {
    const location = useLocation();
    const defaultDescription = descriptionMetaContent.find((item) => item.route === "Default")?.content;
    const [description, setDescription] = useState(defaultDescription);
    const [title, setTitle] = useState("Wonga Official Site - Short Term Loans Online");
    const [noIndex, setNoIndex] = useState(false);
    const [canonicalUrl, setCanonicalUrl] = useState("");

    const removeFallbackMetaDescriptionElement = useCallback(() => {
        // Important: We're using setTimeout here to ensure the code below runs AFTER 
        // the meta changes have been applied to the DOM.
        setTimeout(() => {
            const reactHelmetMetaTags = document.querySelectorAll('meta[name="description"][data-react-helmet="true"]');

            if (reactHelmetMetaTags.length > 0) {
                const fallbackMetaElement = document.querySelector('meta[name="description"][data-fallback="true"]');

                if (fallbackMetaElement) fallbackMetaElement.remove();
            }
        }, 0)
    }, []);

    useEffect(() => {
        const matchedDescription = descriptionMetaContent.find((item) => location.pathname.includes(item.route))?.content;
        setDescription(matchedDescription ?? defaultDescription);

        removeFallbackMetaDescriptionElement();
    }, [location, defaultDescription, removeFallbackMetaDescriptionElement]);

    const setSEO = (meta: { title?: string; description?: string; noIndex?: boolean; canonicalUrl?: string }) => {
        if (meta.title) setTitle(meta.title);
        if (meta.description) setDescription(meta.description);
        setNoIndex(!!meta.noIndex);
        if (meta.canonicalUrl) setCanonicalUrl(meta.canonicalUrl);
    };

    return (
        <SeoContext.Provider value={{ setSEO }}>
            <Helmet>
                <title>{title}</title>
                <meta name="description" content={description} />
                {noIndex && <meta name="robots" content="noindex, nofollow" />}
                {canonicalUrl?.trim() && (
                    <link rel="canonical" href={canonicalUrl.trim()} />
                )}
            </Helmet>
            {children}
        </SeoContext.Provider>
    );
};

export default SeoContextProvider;